import type { OrderInfo, RsuiteSortType } from '@@partner:types:app'

import React from 'react'
import {
  normalizePhoneNumber,
  formatPrice,
  formatDate
} from '@@partner:helpers/util'
import { isEditable } from '@@partner:helpers/order'

import { Link, generatePath } from 'react-router-dom'
import { Button, Tag, ButtonGroup } from 'rsuite'
import LegacyPhoneIcon from '@rsuite/icons/legacy/Phone'
import OrderStatusLabel from '../common/OrderStatusLabel'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable'

import {
  ROUTE_CUSTOMERS_VIEW_CUSTOMER,
  ROUTE_EDIT_BUSINESS_PROFILE,
  ROUTE_ORDERS_EDIT_ORDER,
  ROUTE_ORDERS_VIEW_ORDER
} from '@@partner:config/routes'
import {
  ASSEMBLABLE_STATUSES,
  COMPLETED_STATUSES,
  COURIER_STATUSES,
  ORDER_STATUS_WAITING_FOR_COURIER
} from '@@partner:config/order-status-codes'

import './OrdersTable.scss'

const orderStatusColor = (order: OrderInfo) => {
  const now = new Date().getTime()
  const start = order.deliverySlot
    ? parseInt(order?.deliverySlot?.period?.from)
    : null
  const end = order.deliverySlot
    ? parseInt(order?.deliverySlot?.period?.to)
    : null

  if (!start || !end) {
    return undefined
  }

  const timeLeftBeforeStart = start - now
  const timeLeftBeforeEnd = end - now

  if (ASSEMBLABLE_STATUSES.includes(order.status)) {
    if (timeLeftBeforeStart > 1800) {
      return 'green'
    }
    if (timeLeftBeforeStart > 0) {
      return 'yellow'
    }
    if (timeLeftBeforeStart < 0) {
      return 'red'
    }
  }

  if (COURIER_STATUSES.includes(order.status)) {
    if (timeLeftBeforeEnd > 900) {
      return 'green'
    }
    if (
      timeLeftBeforeEnd > 0 &&
      order.status !== ORDER_STATUS_WAITING_FOR_COURIER
    ) {
      return 'yellow'
    }
    if (
      timeLeftBeforeEnd > 0 &&
      order.status === ORDER_STATUS_WAITING_FOR_COURIER
    ) {
      return 'orange'
    }
    if (timeLeftBeforeEnd < 0) {
      return 'red'
    }
  }

  if (COMPLETED_STATUSES.includes(order.status)) {
    return undefined
  }
}

export interface OrdersTableProps {
  brandSlug: string
  orders: OrderInfo[]
  loading?: boolean
  sortColumn: string
  sortType: RsuiteSortType | undefined
  onSortColumn: (
    sortColumn: string,
    sortType: RsuiteSortType | undefined
  ) => void
}

export default function OrdersTable ({
  brandSlug,
  orders = [],
  loading = false,
  sortColumn,
  sortType,
  onSortColumn
}: OrdersTableProps) {
  const columns: ResponsiveTableColumn<OrderInfo>[] = [
    {
      dataKey: 'id',
      header: 'Заказ',
      sortable: true,
      flexGrow: 2,
      isCardTitle: true,
      render: (order) => (
        <div className="flex flex-col gap-1 items-start">
          <Link 
            to={generatePath(isEditable(order.status) ? ROUTE_ORDERS_EDIT_ORDER : ROUTE_ORDERS_VIEW_ORDER, {
              brandSlug,
              orderId: order.id
            })}
            className="font-bold text-lg hover:text-blue-600"
            style={{ color: orderStatusColor(order) }}
          >
            #{order.id}
          </Link>
          <OrderStatusLabel status={order.status} />
          {order.feedbacks.length > 0 && (
            <Tag color="green" size="sm">Отзыв</Tag>
          )}
        </div>
      )
    },
    {
      dataKey: 'deliverySlot.ts_start',
      header: 'Слот',
      sortable: true,
      flexGrow: 2,
      isCardBadge: true,
      render: (order) => {
        const slot = order.deliverySlot
        if (!slot) {
          return <span className="text-gray-400">—</span>
        }
        return (
          <div className="flex flex-col">
            <span className="font-medium">{slot.slotDate}</span>
            <span className="text-gray-600">{slot.slotTime}</span>
          </div>
        )
      }
    },
    {
      dataKey: 'customer_phone',
      header: 'Покупатель',
      flexGrow: 3,
      render: (order) => (
        <div className="flex flex-col gap-1">
          {order?.businessProfile?.id && (
            <Link 
              to={generatePath(ROUTE_EDIT_BUSINESS_PROFILE, {
                brandSlug,
                profileId: order.businessProfile.id
              })}
              className="text-blue-600 font-medium"
            >
              {order.businessProfile.name}
            </Link>
          )}
          <Link 
            to={generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
              brandSlug,
              customerId: order.customer.id
            })}
            className="font-medium"
          >
            {order.customer.fullName}
          </Link>
          <a 
            href={`tel:${normalizePhoneNumber(order.customer_phone)}`}
            className="text-gray-600 text-sm flex items-center gap-1"
          >
            <LegacyPhoneIcon /> +{order.customer_phone}
          </a>
        </div>
      )
    },
    {
      dataKey: 'shop_id',
      header: 'Магазин',
      flexGrow: 3,
      render: (order) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{order.shop?.name || 'Магазин'}</span>
          <span className="text-gray-600 text-sm">{order.shop?.address}</span>
          {order.assembler && (
            <Tag color="orange" size="sm">
              {order.assembler.first_name} {order.assembler.last_name}
            </Tag>
          )}
        </div>
      )
    },
    {
      dataKey: 'customer_address',
      header: 'Доставка',
      flexGrow: 4,
      render: (order) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm">{order.address}</span>
          {order.courier && (
            <Tag color="blue" size="sm">
              {order.courier.first_name} {order.courier.last_name}
            </Tag>
          )}
        </div>
      )
    },
    {
      dataKey: 'total',
      header: 'Суммы',
      sortable: true,
      flexGrow: 3,
      render: (order) => (
        <div className="flex flex-col text-sm">
          <span>
            Заказ: <strong>{formatPrice(order.total + order.delivery_total)}</strong>
          </span>
          <span className="text-green-600">
            Собрано: <strong>{formatPrice(order.assembledOrderTotal)}</strong>
          </span>
          {order.totalDiscount > 0 && (
            <span className="text-orange-500">
              Скидка: {formatPrice(order.totalDiscount)}
            </span>
          )}
          {order.delivery_total > 0 && (
            <span className="text-gray-500">
              Доставка: {formatPrice(order.delivery_total)}
            </span>
          )}
        </div>
      ),
      renderMobile: (order) => (
        <span>
          {formatPrice(order.assembledOrderTotal)} / {formatPrice(order.total + order.delivery_total)}
        </span>
      )
    }
  ]

  const renderActions = (order: OrderInfo) => (
    <ButtonGroup>
      {isEditable(order.status) ? (
        <Button
          color="cyan"
          appearance="primary"
          as={Link}
          to={generatePath(ROUTE_ORDERS_EDIT_ORDER, {
            brandSlug,
            orderId: order.id
          })}
          size="xs"
        >
          Изменить
        </Button>
      ) : (
        <Button
          color="cyan"
          appearance="primary"
          as={Link}
          to={generatePath(ROUTE_ORDERS_VIEW_ORDER, {
            brandSlug,
            orderId: order.id
          })}
          size="xs"
        >
          Просмотр
        </Button>
      )}
    </ButtonGroup>
  )

  return (
    <div className="OrdersTable">
      <ResponsiveTable<OrderInfo>
        data={orders}
        columns={columns}
        loading={loading}
        rowHeight={120}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        renderActions={renderActions}
        emptyMessage="Нет заказов"
      />
    </div>
  )
}
